import React, { useState } from 'react';
import { X, Sparkles, Brain, Globe, Layers, Zap, Heart, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Churn-prevention modal shown when the user clicks "Cancel subscription".
 *
 * Strategy: lead with what they'll lose, then offer to pause/keep, and only
 * THEN reveal the actual cancel button. Reduces impulse cancels by surfacing
 * the value the user has been getting.
 */
const BENEFITS = [
    { icon: Brain, title: 'AI-powered summaries', detail: 'Bilingual summaries, key insights, "Why this matters"' },
    { icon: Globe, title: 'Auto-categorisation', detail: 'Smart tagging across 18+ topics' },
    { icon: Layers, title: 'Recipe & video extraction', detail: 'Ingredients, steps, transcripts' },
    { icon: Sparkles, title: 'AI Chat across saves', detail: 'Ask questions about your second brain' },
    { icon: Zap, title: '50 Premium AI saves / month', detail: 'Reset on each billing date' },
];

export default function CancelSubscriptionModal({ open, onClose, planName, priceLabel, renewsAt, onCancelled }) {
    const [step, setStep] = useState('benefits'); // 'benefits' | 'confirm'
    const [submitting, setSubmitting] = useState(false);

    if (!open) return null;

    const reset = () => { setStep('benefits'); setSubmitting(false); };
    const handleClose = () => { reset(); onClose?.(); };

    const handleConfirmCancel = async () => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/subscriptions/cancel`, {
                method: 'POST',
                credentials: 'include',
            });
            if (res.ok) {
                const d = await res.json();
                toast.success(
                    d.already_cancelled
                        ? 'Your subscription is already set to end at your renewal date.'
                        : `Cancelled. You'll keep ${planName} access until ${renewsAt}.`,
                    { duration: 6000 }
                );
                reset();
                onCancelled?.();
            } else {
                toast.error('Could not cancel subscription. Please try again or contact support.');
            }
        } catch (e) {
            console.error(e);
            toast.error('Could not cancel subscription.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
            data-testid="cancel-subscription-modal"
        >
            <div
                className="bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-card/95 backdrop-blur border-b border-border px-5 py-3 flex items-center justify-between">
                    <h2 className="text-base font-semibold">
                        {step === 'benefits' ? 'Wait - here\'s what you\'ll miss' : 'Cancel subscription?'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="cancel-modal-close-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {step === 'benefits' && (
                    <div className="p-5 space-y-5" data-testid="cancel-step-benefits">
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50/60 border border-emerald-200">
                            <Heart className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                            <p className="text-sm text-emerald-900">
                                You're on <strong>{planName}</strong>{priceLabel ? ` (${priceLabel})` : ''}. If you cancel now, you'll keep access until <strong>{renewsAt}</strong> - no charge after.
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-3">
                                Features you'll lose
                            </p>
                            <ul className="space-y-2.5">
                                {BENEFITS.map((b) => (
                                    <li key={b.title} className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                            <b.icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">{b.title}</p>
                                            <p className="text-xs text-muted-foreground">{b.detail}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="pt-2 flex flex-col sm:flex-row gap-2">
                            <Button
                                className="flex-1"
                                onClick={handleClose}
                                data-testid="keep-subscription-btn"
                            >
                                <Heart className="w-4 h-4 mr-2" />
                                Keep my subscription
                            </Button>
                            <Button
                                variant="ghost"
                                className="flex-1 text-muted-foreground hover:text-foreground"
                                onClick={() => setStep('confirm')}
                                data-testid="proceed-to-cancel-btn"
                            >
                                Continue cancelling
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'confirm' && (
                    <div className="p-5 space-y-4" data-testid="cancel-step-confirm">
                        <div className="space-y-2">
                            <p className="text-sm text-foreground">
                                We're sorry to see you go. Your subscription will end on <strong>{renewsAt}</strong>.
                            </p>
                            <p className="text-sm text-muted-foreground">
                                You can reactivate any time before that date with one click.
                            </p>
                        </div>

                        <div className="rounded-xl border border-border bg-secondary/30 p-3 text-xs text-muted-foreground space-y-1">
                            <p>· No charge will be made on your renewal date</p>
                            <p>· Your bookmarks stay safe - you keep them on the Basic plan</p>
                            <p>· You'll fall back to 20 lifetime Premium AI saves</p>
                        </div>

                        <div className="pt-1 flex flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setStep('benefits')}
                                disabled={submitting}
                                data-testid="cancel-back-btn"
                            >
                                Back
                            </Button>
                            <Button
                                variant="destructive"
                                className="flex-1"
                                onClick={handleConfirmCancel}
                                disabled={submitting}
                                data-testid="cancel-confirm-btn"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm cancellation'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
